// src/pages/ChatWithAI/ChatMessage.js
import React, { memo, useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeStringify from 'rehype-stringify';
import rehypeSanitize from 'rehype-sanitize';
import ToolProgress from './components/ToolProgress';
import ToolResult from './components/ToolResult';
import SourcesAccordion from './components/SourcesAccordion';

// ✅ Converte Markdown -> HTML (seguro), mesmo padrão usado em SecaoResultado/ExecucaoLogItem,
// para colar no Word/Outlook mantendo a formatação em vez de markdown puro
async function markdownToHtml(markdown) {
  const file = await unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype)
    .use(rehypeSanitize)
    .use(rehypeStringify)
    .process(markdown);

  return String(file);
}

const escapeHtml = (str) => str
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const ChatMessage = memo(({
  message,
  selectedModelId,
  onVerify,
  isVerified,
  toolCalls = [],
  toolResults = new Map(),
  onToolAction
}) => {
  useEffect(() => {
    if (message?.role === 'assistant') {
      console.log('📨 ChatMessage - Mensagem do assistente:', {
        hasContent: !!message.content,
        contentLength: message.content?.length,
        hasSources: !!message.sources,
        sourcesCount: message.sources?.length || 0,
        messageKeys: Object.keys(message)
      });
    }
  }, [message]);

  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!message?.content) return;

    const plainText = message.content;

    try {
      // ✅ Gera HTML (markdown real para o assistente; texto simples preservando quebras de linha para o utilizador)
      const htmlBody = message.role === 'assistant'
        ? await markdownToHtml(plainText)
        : `<p style="white-space:pre-wrap;margin:0;">${escapeHtml(plainText)}</p>`;
      const html = `<!doctype html><html><body>${htmlBody}</body></html>`;

      if (navigator.clipboard?.write && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' })
          })
        ]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plainText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = plainText;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn('⚠️ Falha ao copiar mensagem (rico), a tentar texto simples:', err.message);
      try {
        await navigator.clipboard.writeText(plainText);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (err2) {
        console.warn('⚠️ Falha ao copiar mensagem:', err2.message);
      }
    }
  }, [message?.content, message?.role]);

  if (!message) return null;

  const components = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      if (!inline && match) {
        return (
          <SyntaxHighlighter
            style={vscDarkPlus}
            language={match[1]}
            PreTag="div"
            customStyle={{
              margin: '10px 0',
              borderRadius: '6px',
              fontSize: '13px'
            }}
            {...props}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        );
      }
      return (
        <code className="inline-code-chat-ai" {...props}>
          {children}
        </code>
      );
    },
    p: ({ children }) => <p className="markdown-p-chat-ai">{children}</p>,
    h1: ({ children }) => <h1 className="markdown-h1-chat-ai">{children}</h1>,
    h2: ({ children }) => <h2 className="markdown-h2-chat-ai">{children}</h2>,
    h3: ({ children }) => <h3 className="markdown-h3-chat-ai">{children}</h3>,
    h4: ({ children }) => <h4 className="markdown-h4-chat-ai">{children}</h4>,
    ul: ({ children }) => <ul className="markdown-ul-chat-ai">{children}</ul>,
    ol: ({ children }) => <ol className="markdown-ol-chat-ai">{children}</ol>,
    li: ({ children }) => <li className="markdown-li-chat-ai">{children}</li>,
    a: ({ href, children }) => (
      <a href={href} className="markdown-link-chat-ai" target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
    blockquote: ({ children }) => (
      <blockquote className="markdown-blockquote-chat-ai">{children}</blockquote>
    ),
    table: ({ children }) => (
      <div className="markdown-table-wrapper-chat-ai">
        <table className="markdown-table-chat-ai">{children}</table>
      </div>
    ),
    strong: ({ children }) => <strong className="markdown-strong-chat-ai">{children}</strong>,
    em: ({ children }) => <em className="markdown-em-chat-ai">{children}</em>
  };

  const renderToolCalls = () => {
    if (!toolCalls || toolCalls.length === 0) return null;

    return (
      <div className="tool-calls-container">
        {toolCalls.map((toolCall) => {
          const result = toolResults.get(toolCall.toolCallId);

          return (
            <div key={toolCall.toolCallId} className="tool-call-wrapper">
              <ToolProgress
                toolName={toolCall.toolName}
                steps={result?.reasoningSteps || []}
                status={toolCall.status}
              />

              {toolCall.status === 'completed' && result && result.success && (
                <ToolResult
                  toolName={toolCall.toolName}
                  result={result}
                  onAction={onToolAction}
                />
              )}

              {toolCall.status === 'error' && result && !result.success && (
                <div className="tool-error">
                  <span className="tool-error-icon">❌</span>
                  <span className="tool-error-message">
                    {result.error || 'Erro ao executar tool'}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const hasSources = Array.isArray(message.sources) && message.sources.length > 0;

  // ✅ botão de verificação deve estar “na resposta”
  // Mostra mesmo se content estiver vazio/curto, desde que seja assistant e exista onVerify.
  const shouldShowVerify = message.role === 'assistant' && !message.error && typeof onVerify === 'function';

  return (
    <div className={`message ${message.role} ${message.error ? 'error' : ''}`}>
      <div className="message-header">
        <span className="message-author">
          {message.role === 'user' ? 'Você' : selectedModelId || 'Robot JR'}
        </span>

        {message.role === 'assistant' && (
          <div className="message-info">
            <span className="message-time">
              {new Date(message.timestamp).toLocaleString('pt-PT')}
            </span>
          </div>
        )}
      </div>

      {renderToolCalls()}

      {/* ✅ CONTEÚDO + BOTÃO juntos (o botão “pertence” ao texto, não às fontes) */}
      <div className="assistant-answer-block">
        {message.content ? (
          <div className="message-content">
            {message.role === 'user' ? (
              <p className="user-message-text">{message.content}</p>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={components}
                className="markdown-content-chat-ai"
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>
        ) : (
          message.role === 'assistant' && (
            <div className="message-content message-content--empty">
              <em>Aguarde...</em>
            </div>
          )
        )}

        {shouldShowVerify && (
          <button
            className={`verify-response-btn ${isVerified ? 'verified' : ''}`}
            onClick={onVerify}
            title={isVerified ? 'Ver verificação anterior' : 'Conferir esta resposta'}
          >
            {isVerified ? '✓✓' : '✓'}
          </button>
        )}
      </div>

      {/* ✅ Ações da mensagem (copiar) — abaixo do balão, não interfere no layout do texto */}
      {message.content && (
        <div className="message-actions">
          <button
            type="button"
            className={`copy-message-btn ${copied ? 'copied' : ''}`}
            onClick={handleCopy}
            title={copied ? 'Copiado!' : 'Copiar mensagem'}
          >
            {copied ? (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <rect x="6" y="6" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M4 10H3.5C2.67157 10 2 9.32843 2 8.5V3.5C2 2.67157 2.67157 2 3.5 2H8.5C9.32843 2 10 2.67157 10 3.5V4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>
      )}

      {/* ✅ FONTES em componente separado e colapsado */}
      {hasSources && <SourcesAccordion sources={message.sources} />}

      {/* DEBUG de sources inválidas */}
      {message.sources && !Array.isArray(message.sources) && (
        <div className="message-sources">
          <div className="message-sources-title">⚠️ Sources em formato inválido</div>
          <pre style={{ fontSize: '11px', color: '#ff6b6b' }}>
            {JSON.stringify(message.sources, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.message?.content === nextProps.message?.content &&
    prevProps.message?.role === nextProps.message?.role &&
    prevProps.message?.sources === nextProps.message?.sources &&
    prevProps.isVerified === nextProps.isVerified &&
    prevProps.toolCalls === nextProps.toolCalls
  );
});

ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;
